import { SupabaseClient } from "@supabase/auth-helpers-react";
import { CUSTODIAL_HOLDING_QUERY, CUSTODIAL_WALLETS_TABLE_NAME, CUSTODIAL_HOLDING_TABLE_NAME } from "../../constants";
import { CustodialHolding } from "../../modules/custodialHoldings/types";
import { CollectableHolding } from "../../modules/collectables/types";

export async function getCustodialHoldingsForOwnerAddress(
    sb: SupabaseClient,
    custodialAddress: string
) : Promise<CustodialHolding[]> {
    let { data: holdingRecs, error: holdingError} = await sb
        .from(CUSTODIAL_HOLDING_TABLE_NAME)
        .select(CUSTODIAL_HOLDING_QUERY)
        .eq('owner_address', custodialAddress);
    
    if(holdingError != null || holdingRecs == null) 
        throw Error("Unable to fetch holdings")

    // Process as custodial holdings
    let holdings: CustodialHolding[] = [];

    holdingRecs.map((holdingRec: any) => {
        const custodialHolding: CustodialHolding = {
            id: holdingRec.id,
            ticketAddress: holdingRec.ticket_address,
            tokenId: holdingRec.token_id,
            ownershipValid: holdingRec.ownership_valid,
            isLocked: holdingRec.is_locked,
        }

        holdings.push(custodialHolding);
    });

    return holdings
}


export async function getAllCollectableHoldings(
    sb: SupabaseClient
) : Promise<CustodialHolding[]> {
    let { data: holdingRecs, error: holdingError } = await sb
        .from(CUSTODIAL_HOLDING_TABLE_NAME)
        .select("*")
        .eq('is_collectable', true);

    if(holdingError != null || holdingRecs == null) 
        throw Error("Unable to fetch holdings")

    let holdings: CustodialHolding[] = [];

    holdingRecs.forEach((holdingRec: any) => {
        const custodialHolding: CustodialHolding = {
            id: holdingRec.id,
            ticketAddress: holdingRec.ticket_address,
            tokenId: holdingRec.token_id,
            ownershipValid: holdingRec.ownership_valid,
            isLocked: holdingRec.is_locked,
        }
        holdings.push(custodialHolding);
    });

    return holdings
}

export async function getCustodialHoldingsForUserId(
    sb: SupabaseClient,
    userId: string
) : Promise<CustodialHolding[]> {
    let { data: holdingRecs, error: holdingError} = await sb
        .from(CUSTODIAL_HOLDING_TABLE_NAME)
        .select(CUSTODIAL_HOLDING_QUERY)
        .eq('user_id', userId)
        .eq('is_collectable', false);
    
    if(holdingError != null || holdingRecs == null) 
        throw Error("Unable to fetch holdings")

    // Process as custodial holdings
    let holdings: CustodialHolding[] = [];

    holdingRecs.map((holdingRec: any) => {
        const custodialHolding: CustodialHolding = {
            id: holdingRec.id,
            ticketAddress: holdingRec.ticket_address,
            tokenId: holdingRec.token_id,
            ownershipValid: holdingRec.ownership_valid,
            isLocked: holdingRec.is_locked,
        }

        holdings.push(custodialHolding);
    });

    return holdings
}

export async function getCollectableHoldingsForUserId(
    sb: SupabaseClient,
    userId: string
) : Promise<CollectableHolding[]> {
    let { data: holdingRecs, error: holdingError } = await sb
        .from(CUSTODIAL_HOLDING_TABLE_NAME)
        .select(CUSTODIAL_HOLDING_QUERY)
        .eq('user_id', userId)
        .eq('is_collectable', true);

    // Add log here to see what is retrieved from the database
    console.log('Holdings Records:', holdingRecs);
    console.log('Holdings Error:', holdingError);
    
    if(holdingError != null || holdingRecs == null) 
        throw Error("Unable to fetch holdings")

    // Process as custodial holdings
    let holdings: CollectableHolding[] = [];

    holdingRecs.forEach((holdingRec: any) => {
        const custodialHolding: CollectableHolding = {
            id: holdingRec.id,
            ticketAddress: holdingRec.ticket_address,
            tokenId: holdingRec.token_id,
            ownershipValid: holdingRec.ownership_valid,
            isLocked: holdingRec.is_locked,
        }

        holdings.push(custodialHolding);
    });

    return holdings
}


export async function getCustodialHolding(
    sb: SupabaseClient,
    custodialHoldingRecordId: number
) : Promise<CustodialHolding> {
    let { data: holdingRec, error: holdingError} = await sb
        .from(CUSTODIAL_HOLDING_TABLE_NAME)
        .select(CUSTODIAL_HOLDING_QUERY)
        .eq('id', custodialHoldingRecordId)
        .single();

    console.log(holdingRec);
    console.log(holdingError);
    
    if(holdingError != null || holdingRec == null) 
        throw Error("Unable to fetch holding")

    const custodialHolding: CustodialHolding = {
        id: holdingRec.id,
        ticketAddress: holdingRec.ticket_address,
        tokenId: holdingRec.token_id,
        ownershipValid: holdingRec.ownership_valid,
        isLocked: holdingRec.is_locked,
    }
    return custodialHolding
}

export async function getUserIdFromCustodialAddress(
    sb: SupabaseClient,    
    custodialWalletAddress: string
) {
    const res = await sb.from('custodial_wallets')
            .select('id')
            .eq('wallet_address', custodialWalletAddress)
            .single();

    if(res.error || res.data == null)
        throw Error('Unable to get user id')

    return res.data.id as string
}

export async function getCollectableHolding(
    sb: SupabaseClient,
    collectableHoldingRecordId: number
) : Promise<CustodialHolding> {
    let { data: holdingRec, error: holdingError} = await sb
        .from(CUSTODIAL_HOLDING_TABLE_NAME)
        .select(CUSTODIAL_HOLDING_QUERY)
        .eq('id', collectableHoldingRecordId)
        .eq('is_collectable', true)
        .single();

    console.log(holdingRec);
    console.log(holdingError);
    
    if(holdingError != null || holdingRec == null) 
        throw Error("Unable to fetch holding")

    const custodialHolding: CustodialHolding = {
        id: holdingRec.id,
        ticketAddress: holdingRec.ticket_address,
        tokenId: holdingRec.token_id,
        ownershipValid: holdingRec.ownership_valid,
        isLocked: holdingRec.is_locked,
    }
    return custodialHolding
}

export async function getCustodialWalletForUserId(
    sb: SupabaseClient,
    userId: string
) : Promise<string> {
    // Get user custodial address
    let { data, error} = await sb
        .from(CUSTODIAL_WALLETS_TABLE_NAME)
        .select('wallet_address')
        .eq('id', userId)
        .single();
    
    if(error != null || data == null) 
        throw Error("Unable to fetch wallet address")

    return data.wallet_address
}

// Locks a ticket so it cannot be transferred. Likely to generate a ticket code
export async function vaultTicket(
    sb: SupabaseClient,
    ticketAddress: string,
    tokenId: number,
    userId: string
) {
    let { error } = await sb
        .from(CUSTODIAL_HOLDING_TABLE_NAME)
        .update({
            is_locked: true
        })
        .eq('is_locked', false)
        .eq('ticket_address', ticketAddress)
        .eq('token_id', tokenId)
        .eq('user_id', userId);

    console.log(error);
    
    if(error != null) throw Error("Unable to vault ticket")
}

export async function approveTicketVaulting(
    sb: SupabaseClient,
    ticketAddress: string,
    tokenId: number,
    userId: string
) {
    let { data, error } = await sb
        .from(CUSTODIAL_HOLDING_TABLE_NAME)
        .update({
            user_approved_locking: true
        })
        .eq('is_locked', false)
        .eq('ticket_address', ticketAddress)
        .eq('token_id', tokenId)
        .eq('user_id', userId)
        .eq('user_approved_locking', false)
        .select('id')
        .single();
    
    if(error != null || data == null || data == undefined)
        throw Error("Unable to approve vault ticket")
}

export async function resetUserApproveTicketVaulting(
    sb: SupabaseClient,
    ticketAddress: string,
    tokenId: number,
    userId: string
) {
    let { data, error } = await sb
        .from(CUSTODIAL_HOLDING_TABLE_NAME)
        .update({
            user_approved_locking: false
        })
        .eq('is_locked', true)
        .eq('ticket_address', ticketAddress)
        .eq('token_id', tokenId)
        .eq('user_id', userId)
        .eq('user_approved_locking', true)
        .select('id')
        .single();
    
    if(error != null || data == null || data == undefined)
        throw Error("Reset Failed")
}

// Check if a user has given permission for the database to vault/lock a ticket away for them
export async function checkUserApprovedTicketLocking(
    sb: SupabaseClient,
    ticketAddress: string,
    tokenId: number,
    userId: string
) {
    let { data, error } = await sb
        .from(CUSTODIAL_HOLDING_TABLE_NAME)
        .select('id, user_approved_locking')
        .eq('user_approved_locking', true)
        .eq('ticket_address', ticketAddress)
        .eq('token_id', tokenId)
        .eq('user_id', userId)
        .single();
    
    if(error != null || data == null)
        throw Error("Unable to check lock status")

    return data.user_approved_locking as boolean
}


export async function ticketHoldingIsLocked(
    sb: SupabaseClient,
    ticketAddress: string,
    tokenId: number
) : Promise<boolean> {
    let { data, error } = await sb
        .from(CUSTODIAL_HOLDING_TABLE_NAME)
        .select('is_locked')
        .eq('ticket_address', ticketAddress)
        .eq('token_id', tokenId)
        .single();
    
    if(error != null || data == null) 
        throw Error("Unable to vault ticket")

    return data.is_locked as boolean
}